<template>
  <modal-vue
    :modalActive="modalActive"
    maxWidth="max-w-5xl"
    maxHeight="90vh"
    :hasFooterBorder="true"
    noBodyPadding
    @close="close"
  >
    <template #header>
      <div class="flex items-center gap-3 px-2">
        <div
          class="flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-full bg-primary5"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-[22px] w-[22px] text-primary30"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path :d="mdiShieldAccount" fill="currentColor" />
          </svg>
        </div>
        <div class="flex flex-col">
          <span class="text-lg font-semibold text-textPrimary">{{ modalTitle }}</span>
          <span class="text-[13px] text-gray-500"
            >Permissões efetivas por empresa, unidade e escala</span
          >
        </div>
      </div>
    </template>

    <template #default>
      <!-- Altura fixa: header/toolbar/filtros/footer estáticos e SÓ a árvore rola. -->
      <div class="flex h-[min(620px,70vh)] flex-col">
        <div class="flex flex-wrap gap-2.5 px-6 pb-3.5 pt-4">
          <!-- Mais largo que a busca p/ o contador "+N outras" caber numa linha. -->
          <div class="min-w-[300px] flex-[1.4]">
            <v-autocomplete
              v-model="selectedCompanyIds"
              data-testid="permissions-tree-company-autocomplete"
              :items="availableCompanies"
              item-title="name"
              item-value="id"
              label="Empresas ativas"
              placeholder="Todas as empresas carregadas"
              color="primary20"
              variant="outlined"
              density="compact"
              hide-details
              multiple
              clearable
              :loading="loadingCompanies"
              :prepend-inner-icon="mdiDomain"
              no-data-text="Nenhuma empresa acessível encontrada."
              class="bg-white"
            >
              <template v-slot:item="{ props: itemProps, item }">
                <v-list-item v-bind="itemProps" :title="item.raw.name" subtitle="Empresa" />
              </template>
              <!-- Truncagem: 2 chips + contador (padrão TableChips) para o campo não explodir. -->
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < MAX_VISIBLE_COMPANY_CHIPS"
                  size="small"
                  closable
                  @click:close.stop="removeCompany(item.raw.id)"
                >
                  <span class="max-w-[120px] truncate" :title="item.raw.name">
                    {{ item.raw.name }}
                  </span>
                </v-chip>
                <span
                  v-else-if="index === MAX_VISIBLE_COMPANY_CHIPS"
                  data-testid="permissions-tree-company-overflow"
                  class="whitespace-nowrap text-xs font-medium text-gray-500"
                >
                  +{{ selectedCompanyIds.length - MAX_VISIBLE_COMPANY_CHIPS }}
                  {{
                    selectedCompanyIds.length - MAX_VISIBLE_COMPANY_CHIPS === 1 ? 'outra' : 'outras'
                  }}
                </span>
              </template>
            </v-autocomplete>
          </div>

          <div class="relative h-10 min-w-[180px] flex-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-400"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path :d="mdiMagnify" fill="currentColor" />
            </svg>
            <input
              data-testid="permissions-tree-search-input"
              :value="searchText"
              type="text"
              placeholder="Buscar unidade, escala ou ID"
              class="h-10 w-full rounded-lg border border-gray-300 pl-10 pr-3 text-sm text-textPrimary outline-none placeholder:text-gray-400 focus:border-primary20 focus:ring-2 focus:ring-primary5"
              @input="onSearchInput"
            />
          </div>
        </div>

        <div
          v-if="selectedCompanyIds.length > 0"
          class="flex flex-wrap items-center justify-between gap-2 border-y border-gray-100 bg-gray-50 px-6 py-2.5"
        >
          <segmented-control
            v-model="filters.origin"
            :options="originOptions"
            testIdPrefix="permissions-tree-origin"
          />

          <button
            v-if="permissionsTree.length > 0"
            data-testid="permissions-tree-expand-toggle-button"
            type="button"
            class="flex h-[30px] items-center gap-1.5 rounded-md px-2.5 text-sm font-medium text-primary30 transition-colors hover:bg-primary5"
            @click="toggleExpandAll"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-[18px] w-[18px]"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                :d="anyExpanded ? mdiUnfoldLessHorizontal : mdiUnfoldMoreHorizontal"
                fill="currentColor"
              />
            </svg>
            {{ anyExpanded ? 'Recolher tudo' : 'Expandir tudo' }}
          </button>
        </div>

        <div class="ds-scrollbar min-h-0 flex-1 overflow-y-auto px-4 py-2">
          <permissions-tree-skeleton
            v-if="(loadingTree || loadingCompanies) && permissionsTree.length === 0"
          />

          <report-error-state
            v-else-if="loadError"
            title="Não foi possível carregar"
            :message="loadError"
            @retry="retryAfterError"
          />

          <empty-state
            v-else-if="selectedCompanyIds.length === 0"
            variant="dashed"
            :icon="mdiCursorDefaultClickOutline"
            title="Nenhuma empresa selecionada"
            description="Use o seletor acima para escolher as empresas exibidas na árvore."
          />

          <empty-state
            v-else-if="permissionsTree.length === 0"
            title="Nenhuma permissão encontrada"
            description="Não há permissões para esta seleção."
          />

          <div
            v-else-if="filtersActive && rows.length === 0"
            data-testid="permissions-tree-no-results"
            class="flex h-full flex-col items-center justify-center gap-2 py-10 text-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-8 w-8 text-gray-400"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path :d="mdiMagnifyRemoveOutline" fill="currentColor" />
            </svg>
            <span class="text-sm text-gray-500">Nenhum resultado para os filtros atuais</span>
          </div>

          <div v-else role="tree" class="flex flex-col pb-6">
            <span
              v-if="filtersActive && hasMore"
              data-testid="permissions-tree-filter-partial-warning"
              class="px-2 py-1 text-xs text-gray-500"
            >
              A busca considera apenas os itens já carregados.
            </span>

            <permission-tree-row
              v-for="row in rows"
              :key="row.key"
              :row="row"
              @toggle="toggleNode"
            />

            <permissions-tree-skeleton v-if="isLoadingMore" :rows="3" class="py-2" />

            <div
              v-if="!hasMore && !isLoadingMore"
              class="flex w-full items-center justify-center py-4 text-center"
            >
              <span
                class="rounded-full border border-gray-100 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-500"
              >
                Todos os registros carregados.
              </span>
            </div>

            <div
              v-if="hasMore && !isLoadingMore"
              v-intersect="onIntersect"
              class="pointer-events-none h-8 w-full opacity-0"
            ></div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex w-full flex-wrap items-center justify-between gap-3">
        <div data-testid="permissions-tree-legend" class="flex flex-wrap items-center gap-4">
          <span class="flex items-center gap-1 text-xs text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-3.5 w-3.5 text-primary30"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path :d="mdiAccount" fill="currentColor" />
            </svg>
            Direta: atribuída ao usuário
          </span>
          <span class="flex items-center gap-1 text-xs text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-3.5 w-3.5 text-purple-700"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path :d="mdiAccountGroup" fill="currentColor" />
            </svg>
            Grupo: herdada de um grupo
          </span>
        </div>

        <filled-button
          data-testid="permissions-tree-close-button"
          text="Fechar"
          width="120px"
          height="40px"
          @click="close"
        />
      </div>
    </template>
  </modal-vue>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import {
  mdiAccount,
  mdiAccountGroup,
  mdiCursorDefaultClickOutline,
  mdiDomain,
  mdiMagnify,
  mdiMagnifyRemoveOutline,
  mdiShieldAccount,
  mdiUnfoldLessHorizontal,
  mdiUnfoldMoreHorizontal
} from '@mdi/js'

import Network from '@/Network'
import type {
  AccessibleCompanyOption,
  PermissionTreeItem
} from '@/Network/Types/Responses/PermissionResponses'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import EmptyState from '@/components/designSystem/EmptyState/EmptyState.vue'
import ReportErrorState from '@/components/designSystem/ErrorState/ReportErrorState.vue'
import SegmentedControl from '@/components/designSystem/Inputs/SegmentedControl.vue'
import PermissionsTreeSkeleton from '@/components/designSystem/Skeleton/PermissionsTreeSkeleton.vue'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import { useInfiniteScroll } from '@/composables/useInfiniteScroll'
import {
  EModals,
  useModalStore,
  type ModalMetadata,
  type permissionsTreeModalDataType
} from '@/pinia-store/useModalStore'
import { AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { getApiErrorMessage } from '@/utils/apiError'

import PermissionTreeRow from './components/PermissionTreeRow.vue'
import { countPermissionsByOrigin, flattenPermissionsTree } from './flattenTree'
import type { PermissionsTreeFilterState } from './types'
import { collectExpandableKeys, getNodeKey } from './utils'

const modalStore = useModalStore()

const modalActive = computed(() => modalStore.modals[EModals.PERMISSIONS_TREE])

const isPermissionsTreeMetadata = (
  metadata: ModalMetadata | undefined
): metadata is permissionsTreeModalDataType => {
  if (!metadata) return false
  const candidate = metadata as Partial<permissionsTreeModalDataType>
  return typeof candidate.subjectId === 'number' && typeof candidate.subjectType === 'string'
}

const subjectMetadata = computed<permissionsTreeModalDataType | null>(() =>
  isPermissionsTreeMetadata(modalStore.metadata) ? modalStore.metadata : null
)

const subjectId = computed(() => subjectMetadata.value?.subjectId ?? null)
const subjectType = computed(
  () => subjectMetadata.value?.subjectType ?? AuthorizationResourcesAndSubjects.User
)
const subjectName = computed(() => subjectMetadata.value?.subjectName)

const ITEMS_PER_PAGE = 20
const SEARCH_DEBOUNCE_MS = 150
/** Chips visíveis no select de empresas antes do contador "+N outras". */
const MAX_VISIBLE_COMPANY_CHIPS = 2

const permissionsTree = ref<PermissionTreeItem[]>([])
const availableCompanies = ref<AccessibleCompanyOption[]>([])
// Todas as empresas acessíveis entram selecionadas ao abrir; o seletor serve
// para DES-selecionar (a árvore recarrega com as restantes).
const selectedCompanyIds = ref<number[]>([])
const expandedKeys = ref<Record<string, boolean>>({})

const loadingTree = ref(false)
const loadingCompanies = ref(false)
const loadError = ref<string | null>(null)
const errorSource = ref<'companies' | 'tree'>('tree')
const hasMore = ref(true)

const isUserSubject = computed(() => subjectType.value === AuthorizationResourcesAndSubjects.User)

const modalTitle = computed(() => {
  if (subjectName.value) return `Permissões de ${subjectName.value}`
  return isUserSubject.value ? 'Permissões do Usuário' : 'Permissões do Grupo'
})

// ── Busca e filtros (client-side, sobre os itens já carregados) ─────────────
// A sentinela do infinite scroll continua renderizada com filtro ativo: páginas
// novas entram em `permissionsTree` e são re-achatadas pelo computed. Se a
// visão filtrada ficar vazia, o estado "sem resultados" substitui a lista (e a
// sentinela), pausando o scroll até os filtros mudarem.
const createDefaultFilters = (): PermissionsTreeFilterState => ({ search: '', origin: 'all' })

const filters = ref<PermissionsTreeFilterState>(createDefaultFilters())

const searchText = ref('')
let searchDebounceTimer: ReturnType<typeof setTimeout> | undefined

onBeforeUnmount(() => clearTimeout(searchDebounceTimer))

const onSearchInput = (event: Event): void => {
  searchText.value = (event.target as HTMLInputElement).value
  clearTimeout(searchDebounceTimer)
  searchDebounceTimer = setTimeout(() => {
    filters.value = { ...filters.value, search: searchText.value }
  }, SEARCH_DEBOUNCE_MS)
}

const resetFilters = (): void => {
  clearTimeout(searchDebounceTimer)
  searchText.value = ''
  filters.value = createDefaultFilters()
}

const filtersActive = computed(
  () => filters.value.search.trim().length > 0 || filters.value.origin !== 'all'
)

const rows = computed(() =>
  flattenPermissionsTree(permissionsTree.value, {
    query: filters.value.search,
    origin: filters.value.origin,
    expandedKeys: expandedKeys.value
  })
)

const originCounts = computed(() => countPermissionsByOrigin(permissionsTree.value))

const originOptions = computed(() => [
  { label: 'Todas', value: 'all' as const, count: originCounts.value.all },
  { label: 'Diretas', value: 'direct' as const, count: originCounts.value.direct },
  { label: 'Via grupo', value: 'group' as const, count: originCounts.value.group }
])
// ── fim do bloco de busca e filtros ─────────────────────────────────────────

async function fetchPermissionsTree(isLoadMore = false): Promise<void> {
  if (!subjectId.value || selectedCompanyIds.value.length === 0) {
    permissionsTree.value = []
    loadingTree.value = false
    return
  }

  if (!isLoadMore) loadingTree.value = true
  loadError.value = null

  try {
    const offset = isLoadMore ? permissionsTree.value.length : 0
    const newData = await Network.permission.getSubjectPermissionsTree(
      subjectId.value,
      subjectType.value,
      [...selectedCompanyIds.value],
      offset,
      ITEMS_PER_PAGE
    )

    if (isLoadMore) {
      // Cinto de segurança contra regressão de paginação do backend: se a página
      // não trouxe NENHUM nó novo (por getNodeKey), para o infinite scroll em vez
      // de apendar duplicatas infinitamente.
      const loadedKeys = new Set(permissionsTree.value.map((node) => getNodeKey(node)))
      const freshNodes = newData.filter((node) => !loadedKeys.has(getNodeKey(node)))
      if (freshNodes.length === 0) {
        hasMore.value = false
        return
      }
      permissionsTree.value.push(...freshNodes)
    } else {
      permissionsTree.value = newData
    }

    hasMore.value = newData.length >= ITEMS_PER_PAGE
  } catch (error) {
    errorSource.value = 'tree'
    loadError.value = getApiErrorMessage(error, 'Não foi possível carregar a árvore de permissões.')
    hasMore.value = false
  } finally {
    loadingTree.value = false
  }
}

async function fetchAvailableCompanies(): Promise<void> {
  if (!subjectId.value) return
  loadingCompanies.value = true
  try {
    availableCompanies.value = await Network.permission.getSubjectAccessibleCompanies(
      subjectId.value,
      subjectType.value
    )
    // Abertura já carrega a árvore de TODAS as empresas acessíveis (accordion
    // fechado); o watcher de selectedCompanyIds dispara o fetch.
    selectedCompanyIds.value = availableCompanies.value.map((company) => company.id)
  } catch (error) {
    errorSource.value = 'companies'
    loadError.value = getApiErrorMessage(error, 'Não foi possível carregar a lista de empresas.')
  } finally {
    loadingCompanies.value = false
  }
}

const {
  isLoadingMore,
  onIntersect,
  reset: resetInfiniteScroll
} = useInfiniteScroll({
  loadMore: () => fetchPermissionsTree(true),
  hasMore: () => hasMore.value,
  isBusy: () => loadingTree.value
})

const resetState = (): void => {
  selectedCompanyIds.value = []
  permissionsTree.value = []
  availableCompanies.value = []
  expandedKeys.value = {}
  resetFilters()
  loadError.value = null
  errorSource.value = 'tree'
  hasMore.value = true
  loadingTree.value = false
  loadingCompanies.value = false
  resetInfiniteScroll()
}

watch(modalActive, (isOpen) => {
  if (!isOpen) return
  resetState()
  if (subjectId.value) fetchAvailableCompanies()
})

watch(selectedCompanyIds, () => {
  permissionsTree.value = []
  // Accordion fechado por padrão: a árvore carrega toda recolhida.
  expandedKeys.value = {}
  resetFilters()
  hasMore.value = true
  loadError.value = null
  resetInfiniteScroll()
  if (selectedCompanyIds.value.length > 0) fetchPermissionsTree(false)
})

const removeCompany = (companyId: number): void => {
  selectedCompanyIds.value = selectedCompanyIds.value.filter((id) => id !== companyId)
}

const retryAfterError = (): void => {
  loadError.value = null
  if (errorSource.value === 'companies') {
    fetchAvailableCompanies()
    return
  }
  fetchPermissionsTree(false)
}

const anyExpanded = computed(() => Object.keys(expandedKeys.value).length > 0)

const toggleExpandAll = (): void => {
  if (anyExpanded.value) {
    expandedKeys.value = {}
    return
  }
  expandedKeys.value = Object.fromEntries(
    collectExpandableKeys(permissionsTree.value).map((key) => [key, true])
  )
}

const toggleNode = (key: string): void => {
  // Com filtro ativo o toggle manual é no-op: a expansão exibida é forçada pelo
  // flatten (forceOpen) e o estado do usuário fica preservado para quando os
  // filtros forem limpos.
  if (filtersActive.value) return
  const next = { ...expandedKeys.value }
  if (next[key]) delete next[key]
  else next[key] = true
  expandedKeys.value = next
}

const close = (): void => {
  modalStore.toggleModal(EModals.PERMISSIONS_TREE)
}
</script>
